import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  UploadCloud,
  CheckCircle2,
  Hash,
  ArrowLeft,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useEmployerBrand, BrandLogo, PoweredByBadge } from "@/lib/brand";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function UploadResume() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [error, setError] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { brand } = useEmployerBrand(info?.employer_id);

  useEffect(() => {
    axios
      .get(`${BACKEND}/api/public/resume-attach/${token}/info`)
      .then(({ data }) => setInfo(data))
      .catch((err) =>
        setError(err?.response?.data?.detail || "This link is no longer valid.")
      )
      .finally(() => setLoadingInfo(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please choose a résumé file");
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(
        `${BACKEND}/api/public/resume-attach/${token}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="upload-resume-page"
      className="mx-auto max-w-xl px-6 py-12"
    >
      <div className="flex items-center justify-between">
        {brand?.logo_url ? (
          <BrandLogo brand={brand} className="h-10 w-auto max-w-[140px] object-contain" />
        ) : !brand?.hide_powered_by ? (
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Hireloop PRO home
          </Link>
        ) : (
          <span />
        )}
      </div>

      {loadingInfo ? (
        <div className="mt-12 flex flex-col items-center gap-3 text-sm text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          Looking up your application…
        </div>
      ) : error ? (
        <div
          data-testid="upload-resume-error"
          className="mt-12 flex flex-col items-center gap-3 border border-rose-200 bg-rose-50 px-6 py-10 text-center"
        >
          <AlertTriangle className="h-10 w-10 text-rose-600" />
          <h1 className="font-heading text-xl font-semibold text-rose-900">
            Link unavailable
          </h1>
          <p className="max-w-sm text-sm text-rose-900">{error}</p>
          <p className="text-xs text-rose-800">
            Please reply to the email we sent and we'll send a fresh link.
          </p>
        </div>
      ) : done ? (
        <div
          data-testid="upload-resume-success"
          className="mt-10 flex flex-col items-center gap-3 border border-emerald-200 bg-emerald-50 px-6 py-10 text-center"
        >
          <CheckCircle2 className="h-10 w-10 text-emerald-700" />
          <h1 className="font-heading text-xl font-semibold text-emerald-900">
            Résumé attached to your application
          </h1>
          <p className="max-w-md text-sm text-emerald-900">
            Thanks {info.candidate_name || ""}! Your résumé has been added to
            application <span className="font-mono font-semibold">{info.application_ref}</span> at
            {" "}{info.employer_label}. We'll be in touch.
          </p>
          {!brand?.hide_powered_by && (
            <p className="mt-1 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-emerald-700">
              <Sparkles className="h-3 w-3" /> Reviewed with Hireloop PRO
            </p>
          )}
        </div>
      ) : (
        <>
          <header className="mt-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-700">
              Attach your résumé
            </div>
            <h1 className="mt-2 font-heading text-3xl font-semibold sm:text-4xl">
              Hi {info.candidate_name || "there"},
            </h1>
            <p className="mt-3 text-sm text-black">
              The team at <strong>{info.employer_label}</strong> would like to
              add your résumé to your existing application for{" "}
              <strong>{info.position || "this role"}</strong>. No re-typing
              needed — just attach a PDF, DOCX, or TXT below.
            </p>
            <div className="mt-3 inline-flex items-center gap-1.5 border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600">
              <Hash className="h-3 w-3" />
              Ref {info.application_ref}
            </div>
          </header>

          <form
            onSubmit={submit}
            className="mt-6 space-y-5 border border-slate-200 bg-white p-6"
          >
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-black">
                Résumé file
              </label>
              <Input
                data-testid="upload-resume-file-input"
                type="file"
                accept=".pdf,.docx,.txt"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-2 cursor-pointer"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Max 10 MB · PDF, DOCX, or TXT.
              </p>
            </div>
            <Button
              type="submit"
              data-testid="upload-resume-submit-btn"
              disabled={submitting || !file}
              className="w-full rounded-sm bg-blue-700 py-6 hover:bg-blue-600"
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <UploadCloud className="mr-2 h-4 w-4" />
              )}
              Attach my résumé
            </Button>
            <p className="text-[11px] text-slate-500">
              By submitting, you allow {info.employer_label} to review your
              résumé alongside your existing application. The link is single-use.
            </p>
          </form>
        </>
      )}
      <div className="mt-10 flex justify-end">
        <PoweredByBadge brand={brand} />
      </div>
    </main>
  );
}
